import React, { useEffect, useState } from 'react';
import FormShell from './FormShell';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { useToast } from '../../hooks/use-toast';
import { loadSavedForm, saveForm } from './formUtils';

const DEFAULTS = {
  companyName: '',
  registrationNumber: '',
  financialYearEnd: '',
  turnover: '',
  yearsToFile: '1',
  contactName: '',
  contactEmail: '',
  contactPhone: '',
  notes: '',
};

const steps = ['Company', 'Return Info', 'Review'];

const AnnualReturnsForm = ({ serviceId, onComplete }) => {
  const { toast } = useToast();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(() => loadSavedForm(serviceId) || DEFAULTS);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    saveForm(serviceId, form);
    setSaved(true);
    const t = setTimeout(() => setSaved(false), 1500);
    return () => clearTimeout(t);
  }, [serviceId, form]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const validate = () => {
    if (step === 1 && (!form.companyName || !form.registrationNumber)) {
      toast({
        title: 'Required Fields Missing',
        description: 'Company name and registration number are required.',
        variant: 'destructive',
      });
      return false;
    }
    if (step === 2 && (!form.financialYearEnd || !form.contactEmail)) {
      toast({
        title: 'Required Fields Missing',
        description: 'Financial year end and contact email are required.',
        variant: 'destructive',
      });
      return false;
    }
    return true;
  };

  const next = () => {
    if (!validate()) return;
    setStep((s) => Math.min(s + 1, steps.length));
    window.scrollTo(0, 0);
  };
  const prev = () => {
    setStep((s) => Math.max(s - 1, 1));
    window.scrollTo(0, 0);
  };
  const submit = () => {
    if (!validate()) return;
    onComplete(form);
  };

  return (
    <FormShell
      title="Annual Returns Filing"
      stepLabels={steps}
      currentStep={step}
      onNext={next}
      onPrevious={prev}
      onSubmit={submit}
      autoSaved={saved}
    >
      {step === 1 && (
        <div className="space-y-4">
          <h3 className="text-xl font-semibold text-emerald-800">Company Information</h3>
          <div>
            <Label>Registered Company Name *</Label>
            <Input
              value={form.companyName}
              onChange={(e) => set('companyName', e.target.value)}
            />
          </div>
          <div>
            <Label>CIPC Registration Number *</Label>
            <Input
              value={form.registrationNumber}
              onChange={(e) => set('registrationNumber', e.target.value)}
            />
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4">
          <h3 className="text-xl font-semibold text-emerald-800">Return Details</h3>
          <div>
            <Label>Financial Year End *</Label>
            <Input
              type="date"
              value={form.financialYearEnd}
              onChange={(e) => set('financialYearEnd', e.target.value)}
            />
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <Label>Annual Turnover (R)</Label>
              <Input
                type="number"
                value={form.turnover}
                onChange={(e) => set('turnover', e.target.value)}
              />
            </div>
            <div>
              <Label>Years to File</Label>
              <select
                value={form.yearsToFile}
                onChange={(e) => set('yearsToFile', e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md"
              >
                <option value="1">Current year</option>
                <option value="2">2 years</option>
                <option value="3">3 years</option>
                <option value="4+">4+ years (deregistered)</option>
              </select>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <Label>Contact Name</Label>
              <Input
                value={form.contactName}
                onChange={(e) => set('contactName', e.target.value)}
              />
            </div>
            <div>
              <Label>Contact Email *</Label>
              <Input
                type="email"
                value={form.contactEmail}
                onChange={(e) => set('contactEmail', e.target.value)}
              />
            </div>
          </div>
          <div>
            <Label>Contact Phone</Label>
            <Input
              value={form.contactPhone}
              onChange={(e) => set('contactPhone', e.target.value)}
            />
          </div>
          <div>
            <Label>Notes</Label>
            <textarea
              value={form.notes}
              onChange={(e) => set('notes', e.target.value)}
              className="mt-1 w-full p-2 border border-gray-300 rounded-md min-h-[80px]"
            />
          </div>
        </div>
      )}

      {step === 3 && (
        <Card className="bg-emerald-50 border-emerald-200">
          <CardHeader>
            <CardTitle className="text-emerald-800">Review</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <p><strong>Company:</strong> {form.companyName}</p>
            <p><strong>Reg No:</strong> {form.registrationNumber}</p>
            <p><strong>Year End:</strong> {form.financialYearEnd}</p>
            <p><strong>Turnover:</strong> R{form.turnover || '—'}</p>
            <p><strong>Years to File:</strong> {form.yearsToFile}</p>
            <p><strong>Contact:</strong> {form.contactName} — {form.contactEmail}</p>
          </CardContent>
        </Card>
      )}
    </FormShell>
  );
};

export default AnnualReturnsForm;
